import Joi from "joi";
import { Box, Divider, Stack, Text } from "@chakra-ui/react";
import { useMemo, useEffect, useState } from "react";

import useForm from "hooks/useForm";
import useGlobalState from "hooks/useGlobalState";
import SectionCards from "../components/SectionCards";
import IconPhone from "components/Chakra/Icons/Phone";
import StepButtons from "components/InsertOfferWrapper/components/StepButtons";
import PhoneInput, { JoiPhoneValidator } from "components/Chakra/PhoneInput";
import Button from "components/Chakra/Button";
import AnimatedInput from "components/Chakra/AnimatedInput";
import Checkbox from "components/Chakra/Checkbox";
import CustomLink from "components/Chakra/CustomLink";
import CustomSpinner from "components/CustomSpinner";
import { advertiserType as advertiserTypeConfig } from "@ud/config/prodeje/config";
import { LandlordData } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";
import { parsePhoneStringToObject } from "helpers/phoneUtils";

type LoginModalOpenFn = (props?: any) => void;

type IContact = {
  buttonNextText?: string;
  buttonPrevText?: string;
  landlordData: LandlordData | null;
  insertReduxData: any;
  onSubmit: (data: IFormValue) => void | Promise<void>;
  openLoginModal: LoginModalOpenFn;
};

export type IFormValue = {
  email: string;
  firstName: string;
  lastName: string;
  typeInsert: string;
  phone: { prefix: string; number: string };
  isSendNews: boolean;
  companyName: string;
};

const formSchema = Joi.object({
  email: Joi.string().email({ tlds: false }).required(),
  firstName: Joi.string().required(),
  lastName: Joi.string().required(),
  typeInsert: Joi.string(),
  phone: JoiPhoneValidator(true),
  isSendNews: Joi.boolean(),
  companyName: Joi.string().when("typeInsert", {
    is: advertiserTypeConfig.realEstateBroker.id,
    then: Joi.required(),
    otherwise: Joi.allow(""),
  }),
});

const ContactForm: React.FC<IContact> = ({
  buttonNextText,
  buttonPrevText,
  landlordData,
  insertReduxData,
  onSubmit,
  openLoginModal,
}) => {
  const { user } = useGlobalState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = useMemo(() => {
    const phoneFromLandlord = landlordData?.phone
      ? parsePhoneStringToObject(landlordData.phone)
      : null;

    const phoneFromRedux = insertReduxData?.phone?.number
      ? insertReduxData.phone
      : null;

    return {
      firstName:
        insertReduxData?.firstName ||
        landlordData?.firstName ||
        user?.renter?.firstName ||
        "",
      lastName:
        insertReduxData?.lastName ||
        landlordData?.lastName ||
        user?.renter?.lastName ||
        "",
      email: user?.email || "",
      typeInsert:
        insertReduxData?.typeInsert ||
        (landlordData?.typeId === 1
          ? advertiserTypeConfig.realEstateBroker.id
          : advertiserTypeConfig.privateLandlord.id),
      isSendNews: insertReduxData?.isSendNews ?? false,
      companyName:
        insertReduxData?.companyName || landlordData?.companyName || "",
      phone: phoneFromRedux ||
        phoneFromLandlord ||
        user?.renter?.phone || { number: "", prefix: "+420" },
    };
  }, [insertReduxData, landlordData, user]);

  const {
    handleSubmit,
    getValues,
    registerWithError,
    setValue,
    watch,
    clearErrors,
    errors,
    register,
  } = useForm<IFormValue>(formSchema, {
    onLoginChange: ({ user: globalUser, form }) => {
      if (globalUser) {
        form.setValue("email", globalUser.email);
      }
    },
    keepDataOnSuccess: true,
    defaultValues,
  });

  // Register phone fields so they are included in form data
  useEffect(() => {
    register("phone.prefix");
    register("phone.number");
  }, [register]);

  const typeInsert = watch("typeInsert");
  const phonePrefix = watch("phone.prefix");
  const phoneNumber = watch("phone.number");
  const watchLastName = watch("lastName");
  const watchFirstName = watch("firstName");

  const checkIsBtnDisable = (): boolean =>
    isSubmitting ||
    [typeInsert, phonePrefix, phoneNumber, watchLastName, watchFirstName].some(
      (item) => !item,
    );

  const handleFormSubmit = async (data: IFormValue) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box position="relative">
      {isSubmitting && (
        <CustomSpinner
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex={1000}
        />
      )}
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box textAlign="left">
          <SectionCards
            setValue={(name, value) =>
              setValue(name as keyof IFormValue, value)
            }
            titlePaddingProps={{ pt: "0px", pb: "16px" }}
            formWatch={typeInsert.toString()}
            nameValue="typeInsert"
            items={advertiserTypeConfig}
            gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }}
            title="Kontakt inzerátu"
            icon={<IconPhone boxSize="16px" fillWith="ud-grey-600" />}
            isTitleRow
            description={
              !user && (
                <Text size="medium">
                  Máte účet?{" "}
                  <Button
                    sx={{ textDecoration: "underline" }}
                    onClick={() => {
                      const initialEmail = getValues().email;
                      openLoginModal({ initialEmail });
                    }}
                    variant="link"
                    size="sm"
                    fontSize="16px"
                  >
                    Přihlaste se
                  </Button>
                  .
                </Text>
              )
            }
          />

          <Divider my="32px" borderWidth="1px" borderColor="ud-grey-300" />
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={{ base: "0", md: "10px" }}
          >
            <AnimatedInput
              {...registerWithError("firstName")}
              name="firstName"
              label="Vaše jméno"
              required
            />
            <AnimatedInput
              {...registerWithError("lastName")}
              name="lastName"
              label="Vaše příjmení"
              required
            />
          </Stack>
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={{ base: "0", md: "10px" }}
          >
            {!user && (
              <AnimatedInput
                flex={1}
                {...registerWithError("email")}
                name="email"
                label="E-mail"
                autoComplete="email"
                required
              />
            )}

            <PhoneInput
              flex={1}
              dataTest="offerContact.phone"
              required
              error={errors.phone}
              setValue={setValue}
              clearErrors={clearErrors}
              selectBoxValue={phonePrefix && phonePrefix.toString()}
              phoneNumberValue={phoneNumber && phoneNumber.toString()}
            />
          </Stack>
          {typeInsert === advertiserTypeConfig.realEstateBroker.id && (
            <AnimatedInput
              {...registerWithError("companyName")}
              name="companyName"
              label="Název realitní kanceláře"
              required
            />
          )}
          {user && (
            <Checkbox
              {...registerWithError("isSendNews")}
              pt="4px"
              label={
                <Text>
                  Chci{" "}
                  <CustomLink
                    to="/"
                    target="_blank"
                    borderBottom="1px solid"
                    borderColor="ud-primary"
                  >
                    novinky a tipy
                  </CustomLink>{" "}
                  z realitního světa.
                </Text>
              }
            />
          )}
        </Box>
        <StepButtons
          buttonPrevText={buttonPrevText}
          buttonNextText={buttonNextText}
          checkIsBtnDisable={checkIsBtnDisable}
        />
      </form>
    </Box>
  );
};

export default ContactForm;
